import {
  faLock,
  faMobileScreen,
  faBell,
  faGem,
  faCheck,
  faChevronDown, faBolt, faPersonCirclePlus, faUserCog, faCalendarAlt, faMessages,
  faChartLine, faRouter, faEnvelopeOpenText, faShareFromSquare, faChartNetwork, faIdCard,
  faMessagePen, faMoneyCheckPen,
} from '@fortawesome/pro-solid-svg-icons';
import { SubscriptionChecklistItemType } from '../../../../utility/types/SubscriptionChecklistType';
import {
  setChecklistEntryComplete,
} from '../../../../redux/slices/subscriptionChecklistSlice/subscriptionChecklistSlice';

export const setupChecklistUiIcons = {
  check: faCheck,
  chevronDown: faChevronDown,
  gem: faGem,
};

const setupChecklistItemDisplay = {
  [SubscriptionChecklistItemType.CONNECT_DEVICES]: {
    icon: faRouter,
    title: 'Connect your devices',
    content: [
      'You should see all of your Room Alert Monitors listed on the __Devices__ page. If not, follow these steps: __[How To Add Devices](https://avtech.com/articles/42/)__.',
      '__Check their push status__',
      'Check that your devices are properly communicating with [RoomAlert.com](https://RoomAlert.com) by viewing __your Devices list filtered by Push Status__. (If all of your devices are currently pushing, you won\'t see any listed under the “Not Pushing” filter.)',
    ],
  },
  [SubscriptionChecklistItemType.DOWNLOAD_MOBILE_APP]: {
    icon: faMobileScreen,
    title: 'Download the Room Alert Mobile App',
    content: [
      'The Room Alert Mobile App is available for free from the __[Google Play Store](https://play.google.com/store/apps/details?id=com.avtramobile)__ and __[Apple App Store](https://apps.apple.com/us/app/room-alert/id6447250947)__.',
      { type: 'store-buttons' },
      'The app supports some, but not all, of the features available in your Room Alert Account. For more information, see __[An Overview Of The Room Alert Mobile App](https://avtech.com/articles/25815/)__.',
    ],
  },
  [SubscriptionChecklistItemType.CREATE_ALERT]: {
    icon: faBolt,
    title: 'Create alerts',
    content: [
      'Let us know when we should alert you to sensor conditions. This link shows you how: __[How To Create Alerts](https://avtech.com/articles/223/)__.',
    ],
  },
  [SubscriptionChecklistItemType.CREATE_SENSOR_METRIC]: {
    icon: faChartLine,
    title: 'Graph your sensor data',
    content: [
      'View historical trends by graphing sensor data alongside threshold ranges. Learn more here: __[How To Graph Data](https://avtech.com/articles/21144/)__.',
      '_(This gets marked complete when you save a Data Group.)_',
    ],
  },
  [SubscriptionChecklistItemType.ADD_USER]: {
    icon: faPersonCirclePlus,
    title: 'Invite team members',
    content: [
      'It\'s easy to give access to all the team members that need it. Just follow these steps: __[How To Add Users](https://avtech.com/articles/40/)__.',
    ],
  },
  [SubscriptionChecklistItemType.CONFIGURE_USER_ROLES]: {
    icon: faUserCog,
    title: 'Assign user roles',
    content: [
      'Add an Operator, Viewer, Provisioner or Billing user. For instructions, see __[How To Manage User Access](https://avtech.com/articles/23734/)__.',
    ],
  },
  [SubscriptionChecklistItemType.SCHEDULE_QUIET_TIMES]: {
    icon: faCalendarAlt,
    title: 'Schedule quiet times',
    content: [
      'Quiet times silence alert notifications during specific days or times. See __[How To Schedule Quiet Times For Notifications](https://avtech.com/articles/11432/)__.',
    ],
  },
  [SubscriptionChecklistItemType.ENABLE_REPEAT_NOTIFICATION]: {
    icon: faMessages,
    title: 'Enable repeat notifications',
    content: [
      'Send reminder notifications about triggered alerts. See __[How To Configure Actions To Repeat](https://avtech.com/articles/11430/)__.',
    ],
  },
  [SubscriptionChecklistItemType.CREATE_CUSTOM_ALERT_MESSAGES]: {
    icon: faEnvelopeOpenText,
    title: 'Create custom messages',
    content: [
      'Customize the email and text message notifications sent when an alert triggers or clears. See __[How To Customize Alert Notifications](https://avtech.com/articles/11438/)__.',
    ],
  },
  [SubscriptionChecklistItemType.SHARE_REPORTS]: {
    icon: faShareFromSquare,
    title: 'Share your reports',
    content: [
      'Generate a link that grants read-only access to one of your Data Groups. See __[How To Create Public URLs](https://avtech.com/articles/3552/)__.',
    ],
  },
  [SubscriptionChecklistItemType.CREATE_MONITOR360]: {
    icon: faChartNetwork,
    title: 'Create a Monitor 360 sensor',
    content: [
      'Combine data from multiple physical sensors into a single “virtual” sensor that reflects overall conditions, like averages, differences, or specific combined states. This gives you more context behind alerts—helping you understand not just that something is wrong, but why it\'s happening. See __[How To Create Monitor360 Sensors](https://avtech.com/articles/11406/)__.',
    ],
  },
  [SubscriptionChecklistItemType.ENABLE_SAML]: {
    icon: faIdCard,
    title: 'Authenticate with SAML',
    content: [
      'Single-Sign-On (SSO) gives you the ease of managing users through your own centralized user management system. For instructions, please see __[How To Configure Single-Sign-On](https://avtech.com/articles/23749/)__.',
    ],
  },
  [SubscriptionChecklistItemType.ENABLE_MFA]: {
    icon: faLock,
    title: 'Enable MFA',
    content: [
      'Secure your Room Alert Account login with multi-factor authentication. See __[How To Use Multi-factor Authentication](https://avtech.com/articles/21967/)__.',
    ],
  },
  [SubscriptionChecklistItemType.CONFIRM_BILLING_EMAIL]: {
    icon: faMoneyCheckPen,
    title: 'Confirm your billing info',
    content: [
      'Go to the __Manage Account > Account Status tab__ and enter the best email address for sending renewal & other billing emails.',
    ],
  },
  [SubscriptionChecklistItemType.SHARE_FEEDBACK]: {
    icon: faMessagePen,
    title: 'Share your feedback',
    content: [
      'How was your onboarding experience? Let us know by taking this 2 question survey: __[Room Alert Account Survey](https://www.surveymonkey.com/r/TRW3DQ7)!__. (You\'ll be redirected to SurveyMonkey. Please do not submit sensitive or personal information.)',
    ],
    callToAction: (checklistId, checklistItemId, { dispatch }) => {
      dispatch(setChecklistEntryComplete({ checklistId, checklistItemId }));
    },
  },
};

export function getSetupChecklistItemDisplay(itemId) {
  return setupChecklistItemDisplay[itemId] || { icon: faBell, content: [] };
}

export default setupChecklistItemDisplay;
